import { IonIcon } from '@ionic/react';
import { searchOutline } from 'ionicons/icons';
import React, { useMemo, useState } from 'react';
import {
  AUTOMATION_BLOCK_CATALOG,
  type AutomationBlockDefinition,
} from '../../automation/blockCatalog';
import type { AutomationActionType } from '../../types/AutomationWorkflow.types';
import { getAutomationBlockIcon } from './automationBlockIcons';

const GROUP_ORDER = [
  'File Operations',
  'Serato',
  'Tags',
  'Library',
  'Logic',
  'Agent',
] as const;

const GROUP_LABELS: Record<string, string> = {
  'File Operations': 'File operations',
  Serato: 'Serato',
  Tags: 'Tags & library',
  Library: 'Tags & library',
  Logic: 'Logic',
  Agent: 'Crate Hackers AI',
};

const GROUP_TAG: Record<string, string> = {
  if_else: 'CORE',
  run_agent: 'BETA',
};

export const AutomationActionPickerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSelect: (actionType: AutomationActionType) => void;
}> = ({ open, onClose, onSelect }) => {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = AUTOMATION_BLOCK_CATALOG.filter((block) => {
      if (!normalized) return true;
      return (
        block.title.toLowerCase().includes(normalized) ||
        block.description.toLowerCase().includes(normalized) ||
        block.category.toLowerCase().includes(normalized)
      );
    });

    const buckets = new Map<string, AutomationBlockDefinition[]>();
    filtered.forEach((block) => {
      const key = block.category;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)?.push(block);
    });

    return GROUP_ORDER.filter((category) => buckets.has(category)).map(
      (category) => ({
        category,
        label: GROUP_LABELS[category] ?? category,
        items: buckets.get(category) ?? [],
      }),
    );
  }, [query]);

  if (!open) return null;

  return (
    <div
      className="automation-picker-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="automation-picker-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Add a step"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="automation-picker-head">
          <h2>Add a step</h2>
          <label className="automation-picker-search">
            <IonIcon icon={searchOutline} aria-hidden />
            <input
              type="search"
              value={query}
              placeholder="Search actions — tags, Serato, move, export…"
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
          </label>
        </div>
        <div className="automation-picker-body">
          {grouped.map((group) => (
            <section key={group.category}>
              <p className="automation-picker-group-label">{group.label}</p>
              {group.items.map((block) => (
                <button
                  key={block.type}
                  type="button"
                  className="automation-picker-row"
                  onClick={() => {
                    onSelect(block.type);
                    setQuery('');
                    onClose();
                  }}
                >
                  <span
                    className={`automation-picker-icon automation-picker-icon-${block.type.replace(/_/g, '-')}`}
                  >
                    <IonIcon icon={getAutomationBlockIcon(block.type)} />
                  </span>
                  <span className="automation-picker-copy">
                    <span className="automation-picker-title">
                      {block.title}
                    </span>
                    <span className="automation-picker-desc">
                      {block.description}
                    </span>
                  </span>
                  <span className="automation-picker-tag">
                    {GROUP_TAG[block.type] ?? 'CORE'}
                  </span>
                </button>
              ))}
            </section>
          ))}
          {grouped.length === 0 && (
            <p className="automation-picker-empty">
              No actions match your search.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
